import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Trophy } from "lucide-react";
import { Input } from "../../../../../../components/input";
import { predefinedPoints, type EventEditorFormValues } from "../event.scheme";
import { renderError, RequiredLabel, SectionTitle, type CommonProps } from "./shared";
import { SelectV2 } from "../../../../../../components/select-v2";

type Props = CommonProps & { initialPoints: string };

export function RewardsAttendanceSection({
  initialPoints,
  isSubmitting,
  canEditField,
}: Props) {
  const { control } = useFormContext<EventEditorFormValues>();
  const isPredefinedPoints = predefinedPoints.some((point) => point === Number(initialPoints));
  const [pointsOption, setPointsOption] = useState(
    isPredefinedPoints ? initialPoints : initialPoints ? "custom" : "",
  );
  const [customPoints, setCustomPoints] = useState(isPredefinedPoints ? "" : initialPoints);
  const { t } = useTranslation();
  return (
    <section className="event-form-section">
      <SectionTitle icon={<Trophy className="h-4 w-4" />}>
        {t("event.rewards_attendance")}
      </SectionTitle>
      <Controller
        control={control}
        name="guestAttendanceVerification"
        render={({ field, fieldState }) => (
          <div className="mb-4">
            <RequiredLabel required={false}>
              {t("event.guest_attendance_verification")}
            </RequiredLabel>
            <SelectV2
              value={field.value || ""}
              onChange={field.onChange}
              options={[
                { value: "required_qr_code", label: t("event.required_qr_code") },
                { value: "not_required", label: t("event.not_required") },
              ]}
              placeholder="Select option"
              disabled={isSubmitting || !canEditField("guestAttendanceVerification")}
            />
            {renderError(fieldState.error?.message)}
          </div>
        )}
      />
      <Controller
        control={control}
        name="points"
        render={({ field, fieldState }) => (
          <div>
            <RequiredLabel>{t("event.points")}</RequiredLabel>
            {pointsOption === "custom" ? (
              <Input
                type="number"
                min="0"
                max="1500"
                value={customPoints}
                onChange={(event) => {
                  const value = event.target.value;
                  const numeric = Number.parseInt(value, 10);
                  if (value === "" || (numeric >= 0 && numeric <= 1500)) {
                    setCustomPoints(value);
                    field.onChange(value);
                  }
                }}
                onBlur={() => {
                  if (customPoints === "") {
                    setPointsOption("");
                    field.onChange("");
                  }
                }}
                disabled={isSubmitting || !canEditField("points")}
                autoFocus
              />
            ) : (
                <SelectV2
                  value={pointsOption}
                  onChange={(value) => {
                    setPointsOption(value);
                    setCustomPoints("");
                    field.onChange(value === "custom" ? "" : value);
                  }}
                  options={[
                    ...predefinedPoints.map((value) => ({
                      value: String(value),
                      label: value,
                    })),
                    { value: "custom", label: "Custom Value" },
                  ]}
                  placeholder="Select points amount"
                  disabled={isSubmitting || !canEditField("points")}
                />
            )}
            {renderError(fieldState.error?.message)}
          </div>
        )}
      />
    </section>
  );
}
